'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import { useHasGovernorHubKnowledge } from '@/hooks/useHasGovernorHubKnowledge';

import { useAssistant } from '../context/AssistantContext';

/**
 * Hook that handles URL parameters for opening the AI assistant.
 *
 * Supported URL parameters:
 * - `ai=open` - Opens the assistant
 * - `prompt` - Pre-fills the prompt (requires ai=open)
 *
 * Examples:
 * - `?ai=open` - Just open the assistant
 * - `?ai=open&prompt=Help%20me%20with...` - Open with a prompt
 *
 * Opening is gated on the current user having GovernorHub Knowledge
 */
export const useAssistantUrlParams = () => {
  const searchParams = useSearchParams();
  const { openAssistant, openWithPrompt } = useAssistant();
  const { hasGovernorHubKnowledge } = useHasGovernorHubKnowledge();

  useEffect(() => {
    const aiParam = searchParams.get('ai');

    if (aiParam !== 'open') {
      return;
    }

    if (!hasGovernorHubKnowledge) {
      return;
    }

    const prompt = searchParams.get('prompt');

    if (prompt) {
      openWithPrompt(prompt);
    } else {
      openAssistant();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasGovernorHubKnowledge]);
};
